import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Navigate } from 'react-router-dom'
import { useAuthStore } from '../auth/authStore'
import { PageLoader } from '../../shared/ui/Spinner'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { getPlanningList } from '../../shared/api/planning.api'
import { usePlanningPeriod, type PlanningPeriodState } from './usePlanningPeriod'
import { PlanningPeriodSelector } from './PlanningPeriodSelector'
import { usePlanningSelection } from './usePlanningSelection'
import { AkbTable } from './AkbTable'
import { usePlanningUpsert } from './usePlanningUpsert'
import { PlanningRowView } from './PlanningRowView'
import { formatPlanningAmount, sumMoney } from './planningAmount'
import { MULTI_MONTH_READONLY_TITLE, percent, percentColorClass } from './planningFormat'

function formatRub(n: number): string {
  return n.toLocaleString('ru-RU')
}

const RU_MONTHS_GEN = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
]

function monthLabelRu(monthKey: string): string {
  const m = parseInt(monthKey.slice(5, 7), 10)
  return RU_MONTHS_GEN[m - 1] ?? monthKey
}

const RU_MONTHS_SHORT = [
  'янв',
  'фев',
  'мар',
  'апр',
  'май',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
]
const QUARTER_ROMAN = ['I', 'II', 'III', 'IV']

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// Подпись периода под колонками «План/Факт» — отражает ВЕСЬ выбранный период,
// а не только первый месяц. Год — YTD (январь→текущий месяц) для текущего года,
// полный для прошлых. Квартал — номер + диапазон месяцев. Период — диапазон дат.
function buildPeriodLabel(period: PlanningPeriodState): string {
  if (period.key === 'custom') {
    if (!period.from) return 'период'
    const fmt = (iso: string) => {
      const [, m, d] = iso.split('-').map(Number)
      return `${d} ${RU_MONTHS_SHORT[(m ?? 1) - 1]}`
    }
    return `${fmt(period.from)} – ${fmt(period.to ?? period.from)}`
  }
  if (period.key === 'year') {
    const y = parseInt((period.date ?? '').slice(0, 4), 10)
    const now = new Date()
    const endMonth = y === now.getUTCFullYear() ? now.getUTCMonth() + 1 : 12
    return cap(`${RU_MONTHS_GEN[0]}–${RU_MONTHS_GEN[endMonth - 1]}`)
  }
  if (period.key === 'quarter') {
    const m = parseInt((period.date ?? '').slice(5, 7), 10) // первый месяц квартала
    const q = Math.floor((m - 1) / 3) + 1
    return `${QUARTER_ROMAN[q - 1]} кв · ${RU_MONTHS_SHORT[m - 1]}–${RU_MONTHS_SHORT[m + 1]}`
  }
  // month
  return cap(monthLabelRu(period.date ?? ''))
}

export { monthLabelRu }

const PLANNING_PAGE_ROLES: readonly string[] = [
  'ADMIN',
  'GENERAL_DIRECTOR',
  'ROP',
  'ACCOUNTANT',
  'ASSISTANT',
]

export function PlanningPage() {
  const { user } = useAuthStore()
  const role = user?.role
  // Белый список: ADMIN, гендиректор (просмотр), РОП, бухгалтер (только факт,
  // этап 3 C2) и помощник (просмотр своих менеджеров, 29.09.2026) — тот же,
  // что PLANNING_VIEW_ROLES на API. Прочих — на главную.
  if (!role || !PLANNING_PAGE_ROLES.includes(role)) {
    return <Navigate to="/dashboard" replace />
  }

  return <PlanningPageInner />
}

function PlanningPageInner() {
  const { state: period, setKey, setDate, setCustom } = usePlanningPeriod()
  const periodLabel = useMemo(() => buildPeriodLabel(period), [period])
  const { selection, toggle: toggleSelection, setAll, clear } = usePlanningSelection()
  const { user } = useAuthStore()
  // РОП не может редактировать собственную строку — план/факт ему ставит админ.
  const readonlyManagerId = user?.role === 'ROP' ? (user?.id ?? null) : null
  // Генеральный директор и помощник — раздел целиком в режиме просмотра (бэк
  // всё равно отклонит мутации, но UI не должен предлагать редактирование).
  const isAssistant = user?.role === 'ASSISTANT'
  const readOnlyAll = user?.role === 'GENERAL_DIRECTOR' || isAssistant
  // Бухгалтер вносит только факт — план задают администратор и РОП.
  const planReadonly = user?.role === 'ACCOUNTANT'
  // Чекбоксы выбирают менеджеров для графика на админском дашборде — у
  // помощника такого графика нет, колонку не показываем.
  const showSelection = !isAssistant

  const queryParams = useMemo(() => {
    if (period.key === 'custom') {
      return {
        period: 'custom' as const,
        from: period.from ?? undefined,
        to: period.to ?? undefined,
      }
    }
    return { period: period.key, date: period.date ?? undefined }
  }, [period.key, period.date, period.from, period.to])

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['planning', queryParams],
    queryFn: () => getPlanningList(queryParams),
    // не дёргаем пока для custom не выбраны обе даты
    enabled: period.key !== 'custom' || Boolean(period.from && period.to),
  })

  const [search, setSearch] = useState('')

  // Сохранение с показом ошибки сервера и признаком успеха (usePlanningUpsert).
  const patchPlan = usePlanningUpsert()

  const filteredRows = useMemo(() => {
    if (!data) return []
    const q = search.trim().toLowerCase()
    if (!q) return data.rows
    return data.rows.filter(
      (r) => r.managerName.toLowerCase().includes(q) || r.managerEmail.toLowerCase().includes(q),
    )
  }, [data, search])

  const toggleAll = () => {
    if (!data) return
    // Toggle-all действует на ТЕКУЩИЙ отфильтрованный список — удобно при поиске.
    const all = filteredRows.map((r) => r.managerId)
    const visibleSelected = all.every((id) => selection.has(id))
    if (visibleSelected) clear()
    else setAll(all)
  }

  if (isLoading) return <PageLoader />
  if (isError || !data) {
    return (
      <div className="space-y-3">
        <h1 className="text-xl font-semibold text-gray-900">Планирование</h1>
        <ErrorMessage error={error} />
      </div>
    )
  }

  const totalPlan = sumMoney(filteredRows.map((r) => r.planAmount))
  const totalFact = sumMoney(filteredRows.map((r) => r.factAmount))
  const totalQuoted = filteredRows.reduce((s, r) => s + r.quotedAmount, 0)
  const totalOrdered = filteredRows.reduce((s, r) => s + r.orderedAmount, 0)
  const allSelected =
    filteredRows.length > 0 && filteredRows.every((r) => selection.has(r.managerId))
  // Квартал / год / период из нескольких месяцев: в полях — сумма месяцев, а
  // запись шла бы в первый месяц и портила его. Такие суммы — только просмотр.
  const multiMonth = data.period.months.length > 1
  const focusMonth = data.period.months[0]!

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Планирование</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            {isAssistant
              ? 'План и факт прикреплённых к вам менеджеров — только просмотр. План задают администратор и РОП, факт вносят они же или бухгалтерия.'
              : 'План и факт по менеджерам. Чекбокс управляет графиком на странице «Дашборд».'}
          </p>
          {!readOnlyAll && !multiMonth && (
            <p className="text-xs text-gray-500 mt-0.5">
              Суммы можно вставлять с копейками: «1 140 415,32» или «1500.50». «+50 000» прибавит к
              текущему значению, «−50 000» вычтет. Enter — сохранить, Esc — отменить.
            </p>
          )}
        </div>
        <PlanningPeriodSelector
          periodKey={period.key}
          date={period.date}
          from={period.from}
          to={period.to}
          onPeriodChange={setKey}
          onDateChange={setDate}
          onCustomChange={setCustom}
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск менеджера…"
          className="w-full max-w-xs border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
        />
        {search && (
          <span className="text-xs text-gray-400">
            найдено {filteredRows.length} из {data.rows.length}
          </span>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                {showSelection && (
                  <th className="px-3 py-2 w-10 text-center">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      title="Все/никого"
                    />
                  </th>
                )}
                <th className="px-3 py-2 text-left">Менеджер</th>
                <th className="px-3 py-2 text-right w-36">
                  План, ₽<div className="text-[10px] text-gray-400 font-normal">{periodLabel}</div>
                </th>
                <th className="px-3 py-2 text-right w-36">
                  Факт, ₽<div className="text-[10px] text-gray-400 font-normal">{periodLabel}</div>
                </th>
                <th className="px-3 py-2 text-right w-16">%</th>
                <th className="px-3 py-2 text-right w-44">КП выставлено, ₽</th>
                <th className="px-3 py-2 text-right w-40">В заказ, ₽</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <PlanningRowView
                  key={row.managerId}
                  row={row}
                  isSelected={selection.has(row.managerId)}
                  showSelection={showSelection}
                  readonly={readOnlyAll || multiMonth || row.managerId === readonlyManagerId}
                  readonlyTitle={multiMonth ? MULTI_MONTH_READONLY_TITLE : undefined}
                  planReadonly={planReadonly}
                  onToggle={() => toggleSelection(row.managerId)}
                  onPatch={(planAmount, factAmount) =>
                    patchPlan({
                      managerId: row.managerId,
                      periodMonth: focusMonth,
                      planAmount,
                      factAmount,
                    })
                  }
                />
              ))}
              {filteredRows.length === 0 && (
                <tr>
                  <td
                    colSpan={showSelection ? 7 : 6}
                    className="px-3 py-6 text-center text-gray-400"
                  >
                    {isAssistant && data.rows.length === 0
                      ? 'К вам ещё не прикреплены менеджеры — это настраивает администратор'
                      : 'Менеджеров не найдено'}
                  </td>
                </tr>
              )}
            </tbody>
            {filteredRows.length > 0 && (
              <tfoot className="bg-gray-50 text-gray-700 font-medium">
                <tr>
                  <td colSpan={showSelection ? 2 : 1} className="px-3 py-2 text-right">
                    Итого:
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatPlanningAmount(totalPlan, 'money')}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatPlanningAmount(totalFact, 'money')}
                  </td>
                  <td className={`px-3 py-2 text-right ${percentColorClass(totalPlan, totalFact)}`}>
                    {percent(totalPlan, totalFact)}
                  </td>
                  <td className="px-3 py-2 text-right">{formatRub(totalQuoted)}</td>
                  <td className="px-3 py-2 text-right">{formatRub(totalOrdered)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {multiMonth && (
        <p className="text-xs text-gray-400">
          В таблице сумма за {data.period.months.length} мес. Править план и факт можно только в
          режиме «Месяц» — так сумма точно попадёт в нужный месяц.
        </p>
      )}

      <AkbTable
        rows={filteredRows}
        periodLabel={periodLabel}
        multiMonth={multiMonth}
        readonlyManagerId={readonlyManagerId}
        readOnlyAll={readOnlyAll}
        planReadOnly={planReadonly}
        onPatch={(managerId, akbPlanCount, akbFactCount) =>
          patchPlan({
            managerId,
            periodMonth: focusMonth,
            akbPlanCount,
            akbFactCount,
          })
        }
      />
    </div>
  )
}
